/**
 * Toast Manager Service
 * 
 * Centralized toast management service following the Service Pattern.
 * Handles toast deduplication, lifecycle management, and provides a clean API.
 * 
 * Architecture:
 * - Singleton pattern for global state management
 * - Separation of concerns (deduplication logic separate from toast display)
 * - Event-driven cleanup via onDismiss callbacks
 */

class ToastManager {
  constructor() {
    // Private state: Track active toast messages (normalized)
    // Using Map to track reference count for each message
    // Key: normalized message, Value: reference count
    this.activeMessages = new Map();
    
    // Bind methods to maintain context
    this.tryRegisterMessage = this.tryRegisterMessage.bind(this);
    this.unregisterMessage = this.unregisterMessage.bind(this);
    this.clearAll = this.clearAll.bind(this);
    this.normalizeMessage = this.normalizeMessage.bind(this);
  }

  /**
   * Normalize message for consistent deduplication
   * Trims whitespace to handle cases where same message has different whitespace
   * @param {string} message - The message to normalize
   * @returns {string} Normalized message
   * @private
   */
  normalizeMessage(message) {
    if (!message || typeof message !== 'string') {
      return '';
    }
    // Trim whitespace for consistent comparison
    // This handles cases where same message has leading/trailing whitespace differences
    return message.trim();
  }

  /**
   * Try to register a message atomically (check-and-set operation)
   * This prevents race conditions when multiple calls happen in the same event loop tick
   * Returns true if registration succeeded, false if message was already active
   * 
   * This is the preferred method for registering messages to prevent rapid successive
   * calls from bypassing deduplication.
   * 
   * @param {string} message - The toast message
   * @returns {boolean} True if message was registered (wasn't active), false if already active
   * @throws {Error} If message is invalid
   */
  tryRegisterMessage(message) {
    const normalized = this.normalizeMessage(message);
    if (!normalized) {
      throw new Error('ToastManager: Invalid message provided (empty or non-string)');
    }
    
    // Atomic check-and-set: Check if message is active, and if not, register it
    // This prevents race conditions where multiple calls in the same tick
    // all pass the check before any register
    const currentCount = this.activeMessages.get(normalized) || 0;
    
    if (currentCount > 0) {
      // Message is already active, registration failed
      return false;
    }
    
    // Message is not active, register it atomically
    this.activeMessages.set(normalized, 1);
    return true;
  }

  /**
   * Unregister a message (called when toast is dismissed)
   * Message is normalized before unregistration
   * Uses reference counting - only removes message when count reaches 0
   * This prevents race conditions where one toast's cleanup might affect another
   * @param {string} message - The toast message
   */
  unregisterMessage(message) {
    const normalized = this.normalizeMessage(message);
    if (normalized) {
      const currentCount = this.activeMessages.get(normalized) || 0;
      if (currentCount > 1) {
        // Decrement reference count
        this.activeMessages.set(normalized, currentCount - 1);
      } else {
        // Remove completely when count reaches 0
        this.activeMessages.delete(normalized);
      }
    }
  }

  /**
   * Clear all registered messages
   * Useful for cleanup or testing
   */
  clearAll() {
    this.activeMessages.clear();
  }

  /**
   * Get count of active messages
   * @returns {number} Number of active toast messages
   */
  getActiveCount() {
    return this.activeMessages.size;
  }

  /**
   * Get reference count for a message (for debugging)
   * @param {string} message - The message to check
   * @returns {number} Reference count (0 if not registered)
   */
  getMessageRefCount(message) {
    const normalized = this.normalizeMessage(message);
    if (!normalized) {
      return 0;
    }
    return this.activeMessages.get(normalized) || 0;
  }
}

// Export singleton instance
export const toastManager = new ToastManager();

